import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { 
  Layout, Menu, Button, Table, Card, Tag, message, Modal, 
  Dropdown, Menu as DropdownMenu, Descriptions, Row, Col,
  Divider, Select, Form, Input, ColorPicker, Upload, Switch,
  Checkbox, TimePicker, Pagination, Space, Tooltip
} from 'antd';
import SchoolAppsManagement from './SchoolAppsManagement';
import { 
  SyncOutlined, PoweroffOutlined, SettingsOutlined, 
  CopyOutlined, PlayCircleOutlined, ClockCircleOutlined,
  LayoutOutlined, AppstoreOutlined, LinkOutlined, LaptopOutlined
} from '@ant-design/icons';
import api from '../api/axios';
import ThirdPartyAppsPage from './ThirdPartyAppsPage';
import SchoolPageConfigPage from './SchoolPageConfigPage';
import { DEVICE_TYPES, isClassBoard } from '../utils/deviceTypes';

const { Header, Content, Sider } = Layout;

const SchoolDashboard = ({ teacher, onLogout }) => {
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [controlModalVisible, setControlModalVisible] = useState(false);
  const [controlType, setControlType] = useState('power');
  const [pageConfigModalVisible, setPageConfigModalVisible] = useState(false);
  const [pageConfig, setPageConfig] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [loading, setLoading] = useState(false);
  // 设备详情相关状态
  const [deviceDetailModalVisible, setDeviceDetailModalVisible] = useState(false);
  const [deviceDetailData, setDeviceDetailData] = useState(null);
  const [deviceStudentStatus, setDeviceStudentStatus] = useState(null);
  const [queryCardNumber, setQueryCardNumber] = useState('');
  const [queryResult, setQueryResult] = useState(null);
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  // 学生应用配置相关状态
  const [deviceApps, setDeviceApps] = useState([]);
  const [deviceClassApps, setDeviceClassApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [studentAppModalVisible, setStudentAppModalVisible] = useState(false);
  const [classAppModalVisible, setClassAppModalVisible] = useState(false);
  // 新功能状态
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [powerConfigModalVisible, setPowerConfigModalVisible] = useState(false);
  const [powerConfigForm, setPowerConfigForm] = useState({
    power_on_time: '07:30:00',
    power_off_time: '18:00:00',
    week_days: ['1', '2', '3', '4', '5'],
    is_enabled: true
  });
  const [devicePowerConfig, setDevicePowerConfig] = useState(null);
  const [thirdPartyApps, setThirdPartyApps] = useState([]);
  const [currentPage, setCurrentPage] = useState('class-board-devices');
  const [commands, setCommands] = useState([]);
  const [commandsPage, setCommandsPage] = useState(1);
  const [commandsTotal, setCommandsTotal] = useState(0);
  const [commandsLoading, setCommandsLoading] = useState(false);
  // 风格选项
  const styleOptions = [
    { value: 'natural', label: '竖屏自然风' }
  ];
  // 星期选项
  const weekDayOptions = [
    { value: '1', label: '周一' },
    { value: '2', label: '周二' },
    { value: '3', label: '周三' },
    { value: '4', label: '周四' },
    { value: '5', label: '周五' },
    { value: '6', label: '周六' },
    { value: '7', label: '周日' }
  ];
  // 时间字符串转dayjs对象
  const timeStrToDate = (timeStr) => {
    if (!timeStr) return null;
    const parts = timeStr.split(':');
    return dayjs().hour(parseInt(parts[0]) || 0).minute(parseInt(parts[1]) || 0).second(parseInt(parts[2]) || 0);
  };

  // 模块配置 - 固定尺寸：课表大、奖卡小、行规小、通知中、荣誉小、考勤小、午餐中
  const availableModules = [
    { id: 'schedule', name: '课表', icon: '📅', size: 'large', color1: '#1890ff', color2: '#096dd9', gradientType: 'horizontal' },
    { id: 'award', name: '奖卡', icon: '🏆', size: 'small', color1: '#fa8c16', color2: '#d46b08', gradientType: 'horizontal' },
    { id: 'behavior', name: '行规', icon: '📋', size: 'small', color1: '#52c41a', color2: '#389e0d', gradientType: 'horizontal' },
    { id: 'notice', name: '通知', icon: '📢', size: 'medium', color1: '#722ed1', color2: '#531dab', gradientType: 'horizontal' },
    { id: 'honor', name: '荣誉', icon: '⭐', size: 'small', color1: '#eb2f96', color2: '#c41d7f', gradientType: 'horizontal' },
    { id: 'attendance', name: '考勤', icon: '📊', size: 'small', color1: '#13c2c2', color2: '#08979c', gradientType: 'horizontal' },
    { id: 'lunch', name: '午餐', icon: '🍱', size: 'medium', color1: '#fa541c', color2: '#d3380d', gradientType: 'horizontal' }
  ];

  const gradientTypeOptions = [
    { value: 'vertical', label: '上下' },
    { value: 'horizontal', label: '左右' },
    { value: 'radial', label: '内外' }
  ];

  const moduleColorOptions = [
    { value: '#1890ff', label: '蓝色' },
    { value: '#52c41a', label: '绿色' },
    { value: '#fa8c16', label: '橙色' },
    { value: '#f5222d', label: '红色' },
    { value: '#722ed1', label: '紫色' },
    { value: '#eb2f96', label: '粉色' },
    { value: '#13c2c2', label: '青色' },
    { value: '#fa541c', label: '红色' },
    { value: '#2f54eb', label: '深蓝' }
  ];

  const themeColors = [
    { name: '星空蓝', bgGradient: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', textColor: '#ffffff', borderColor: '#4a9eff' },
    { name: '森林绿', bgGradient: 'linear-gradient(135deg, #1e3a2f 0%, #2d5a44 50%, #1a4d35 100%)', textColor: '#e8f5e9', borderColor: '#66bb6a' },
    { name: '暖阳橙', bgGradient: 'linear-gradient(135deg, #3d2914 0%, #5d4037 50%, #4e342e 100%)', textColor: '#fff8e1', borderColor: '#ffb74d' },
    { name: '玫瑰红', bgGradient: 'linear-gradient(135deg, #4a1942 0%, #880e4f 50%, #c2185b 100%)', textColor: '#fce4ec', borderColor: '#f48fb1' },
    { name: '薰衣草紫', bgGradient: 'linear-gradient(135deg, #311b92 0%, #5e35b1 50%, #7e57c2 100%)', textColor: '#ede7f6', borderColor: '#b388ff' },
    { name: '海洋蓝', bgGradient: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 50%, #1976d2 100%)', textColor: '#e3f2fd', borderColor: '#64b5f6' },
    { name: '落日金', bgGradient: 'linear-gradient(135deg, #4e342e 0%, #6d4c41 50%, #8d6e63 100%)', textColor: '#fff9c4', borderColor: '#ffd54f' },
    { name: '晨曦粉', bgGradient: 'linear-gradient(135deg, #880e4f 0%, #ad1457 50%, #c2185b 100%)', textColor: '#fce4ec', borderColor: '#f8bbd9' },
    { name: '清新青', bgGradient: 'linear-gradient(135deg, #006064 0%, #00838f 50%, #00acc1 100%)', textColor: '#e0f7fa', borderColor: '#4dd0e1' }
  ];

  const pageDeviceTypeMap = {
    'class-board-devices': DEVICE_TYPES.CLASS_BOARD,
    'moral-machine-devices': DEVICE_TYPES.MORAL_MACHINE
  };
  const currentDeviceType = pageDeviceTypeMap[currentPage] || DEVICE_TYPES.CLASS_BOARD;
  const currentIsClassBoard = isClassBoard(currentDeviceType);

  const fetchDevices = async () => {
    try {
      const params = { type: currentDeviceType };
      if (currentIsClassBoard && selectedClass) {
        params.classId = selectedClass;
      }
      const response = await api.get('/school/devices', { params });
      setDevices(response.data);
    } catch (error) {
      message.error('获取设备列表失败');
    }
  };

  useEffect(() => {
    fetchDevices();
  }, [selectedClass, currentDeviceType]);

  useEffect(() => {
    setSelectedClass(null);
    setSelectedDevices([]);
    setSelectedDevice(null);
  }, [currentDeviceType]);

  const handleControl = async (action) => {
    setLoading(true);
    try {
      let url = `/school/devices/${selectedDevice.id}/control/${controlType}`;
      const data = controlType === 'app' ? { action, appName: 'com.example.app' } : { action };
      await api.post(url, data);
      message.success(`指令已发送: ${action}`);
      setControlModalVisible(false);
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchPageConfig = async (device) => {
    try {
      const response = await api.get(`/school/devices/${device.id}/page-config`);
      let config = response.data;
      // 确保模块有双色和渐变类型
      if (config.modules) {
        config.modules = config.modules.map(mod => {
          const defaultMod = availableModules.find(m => m.id === mod.id);
          return {
            ...mod,
            color1: mod.color1 || mod.color || defaultMod?.color1 || '#1890ff',
            color2: mod.color2 || defaultMod?.color2 || '#096dd9',
            gradientType: mod.gradientType || 'horizontal'
          };
        });
      }
      setPageConfig(config);
      setSelectedDevice(device);
      setPageConfigModalVisible(true);
    } catch (error) {
      message.error('获取页面配置失败');
    }
  };

  // 获取设备详情数据
  const fetchDeviceDetailData = async (device) => {
    try {
      const response = await api.get(`/school/devices/${device.id}/detail`);
      setDeviceDetailData(response.data);
      if (isClassBoard(response.data.type)) {
        // 获取同步状态
        fetchSyncStatus(device.id);
        // 获取设备学生数据状态
        fetchDeviceStudentStatus(device.id);
      } else {
        setSyncStatus(null);
        setDeviceStudentStatus(null);
        setQueryResult(null);
      }
      // 获取设备开关机配置
      fetchDevicePowerConfig(device.id);
      // 获取设备指令日志
      fetchDeviceCommands(device.id, 1);
    } catch (error) {
      message.error('获取设备详情失败');
    }
  };

  // 获取同步状态
  const fetchSyncStatus = async (deviceId) => {
    try {
      const response = await api.get(`/school/devices/${deviceId}/sync/status`);
      setSyncStatus(response.data);
    } catch (error) {
      console.error('获取同步状态失败');
    }
  };

  // 获取设备学生数据状态
  const fetchDeviceStudentStatus = async (deviceId) => {
    try {
      const response = await api.get(`/school/devices/${deviceId}/students/status`);
      setDeviceStudentStatus(response.data);
    } catch (error) {
      console.error('获取设备学生数据状态失败');
    }
  };

  // 触发学生数据同步
  const handleSyncStudents = async () => {
    setLoading(true);
    try {
      await api.post(`/school/devices/${selectedDevice.id}/sync/students`, { sync_type: 'full', force: true });
      message.success('同步任务已创建');
      setSyncModalVisible(false);
      fetchSyncStatus(selectedDevice.id);
    } catch (error) {
      message.error('同步失败');
    } finally {
      setLoading(false);
    }
  };

  // 查询学生信息
  const handleQueryStudent = async () => {
    if (!queryCardNumber) {
      message.warning('请输入学生卡号');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post(`/school/devices/${selectedDevice.id}/students/query`, { card_number: queryCardNumber });
      setQueryResult(response.data);
    } catch (error) {
      message.error('查询学生信息失败');
    } finally {
      setLoading(false);
    }
  };

  // 触发设备上报学生数据
  const handleTriggerStudentReport = async () => {
    setLoading(true);
    try {
      await api.post(`/school/devices/${selectedDevice.id}/control/student-report`);
      message.success('上报学生数据指令已发送');
    } catch (error) {
      message.error('发送指令失败');
    } finally {
      setLoading(false);
    }
  };

  const savePageConfig = async () => {
    setLoading(true);
    try {
      await api.post(`/school/devices/${selectedDevice.id}/page-config`, pageConfig);
      message.success('页面配置已保存');
      setPageConfigModalVisible(false);
    } catch (error) {
      message.error('保存失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取设备学生应用
  const fetchDeviceApps = async (deviceId) => {
    setAppsLoading(true);
    try {
      const response = await api.get(`/school/devices/${deviceId}/student-apps`);
      setDeviceApps(response.data);
    } catch (error) {
      message.error('获取设备学生应用失败');
    } finally {
      setAppsLoading(false);
    }
  };

  // 更新设备学生应用
  const handleUpdateDeviceApps = async () => {
    setAppsLoading(true);
    try {
      const enabledAppIds = deviceApps.filter(app => app.is_enabled).map(app => app.id);
      await api.put(`/school/devices/${selectedDevice.id}/student-apps`, { app_ids: enabledAppIds });
      message.success('更新设备学生应用成功');
      setStudentAppModalVisible(false);
    } catch (error) {
      message.error('更新设备学生应用失败');
    } finally {
      setAppsLoading(false);
    }
  };

  // 获取设备班级应用
  const fetchDeviceClassApps = async (deviceId) => {
    setAppsLoading(true);
    try {
      const response = await api.get(`/school/devices/${deviceId}/class-apps`);
      setDeviceClassApps(response.data);
    } catch (error) {
      message.error('获取设备班级应用失败');
    } finally {
      setAppsLoading(false);
    }
  };

  // 更新设备班级应用
  const handleUpdateDeviceClassApps = async () => {
    setAppsLoading(true);
    try {
      const enabledAppIds = deviceClassApps.filter(app => app.is_enabled).map(app => app.id);
      await api.put(`/school/devices/${selectedDevice.id}/class-apps`, { app_ids: enabledAppIds });
      message.success('更新设备班级应用成功');
      setClassAppModalVisible(false);
    } catch (error) {
      message.error('更新设备班级应用失败');
    } finally {
      setAppsLoading(false);
    }
  };

  // 切换学生应用启用状态
  const toggleAppEnabled = (appId) => {
    setDeviceApps(deviceApps.map(app => {
      if (app.id === appId) {
        return { ...app, is_enabled: !app.is_enabled };
      }
      return app;
    }));
  };

  // 切换班级应用启用状态
  const toggleClassAppEnabled = (appId) => {
    setDeviceClassApps(deviceClassApps.map(app => {
      if (app.id === appId) {
        return { ...app, is_enabled: !app.is_enabled };
      }
      return app;
    }));
  };

  // 切换第三方应用启用状态
  const toggleThirdPartyAppEnabled = (appId) => {
    setThirdPartyApps(thirdPartyApps.map(app => {
      if (app.id === appId) {
        return { ...app, is_enabled: !app.is_enabled };
      }
      return app;
    }));
  };

  // 获取学校第三方应用
  const fetchThirdPartyApps = async () => {
    try {
      const response = await api.get(`/school/school/${teacher.school_id}/third-party-apps`);
      setThirdPartyApps(response.data);
    } catch (error) {
      message.error('获取第三方应用失败');
    }
  };

  // 获取设备开关机配置
  const fetchDevicePowerConfig = async (deviceId) => {
    try {
      const response = await api.get(`/school/devices/${deviceId}/power-config`);
      setDevicePowerConfig(response.data);
    } catch (error) {
      message.error('获取设备开关机配置失败');
    }
  };

  // 更新设备开关机配置
  const handleUpdateDevicePowerConfig = async () => {
    console.log('handleUpdateDevicePowerConfig called');
    console.log('selectedDevice:', selectedDevice);
    console.log('devicePowerConfig:', devicePowerConfig);
    setLoading(true);
    try {
      if (!selectedDevice || !devicePowerConfig) {
        throw new Error('设备信息或配置为空');
      }
      const weekDaysValue = Array.isArray(devicePowerConfig.week_days) ? devicePowerConfig.week_days.join(',') : devicePowerConfig.week_days;
      console.log('Sending data:', {
        power_on_time: devicePowerConfig.power_on_time,
        power_off_time: devicePowerConfig.power_off_time,
        week_days: weekDaysValue,
        is_enabled: devicePowerConfig.is_enabled ? 1 : 0
      });
      await api.put(`/school/devices/${selectedDevice.id}/power-config`, {
        power_on_time: devicePowerConfig.power_on_time,
        power_off_time: devicePowerConfig.power_off_time,
        week_days: weekDaysValue,
        is_enabled: devicePowerConfig.is_enabled ? 1 : 0
      });
      message.success('设备开关机配置已更新');
    } catch (error) {
      console.error('更新失败原因:', error.message);
      message.error('更新失败');
    } finally {
      setLoading(false);
    }
  };

  // 批量设置开关机配置
  const handleBatchPowerConfig = async () => {
    setLoading(true);
    try {
      await api.put('/school/devices/batch/power-config', {
        device_ids: selectedDevices,
        power_on_time: powerConfigForm.power_on_time,
        power_off_time: powerConfigForm.power_off_time,
        week_days: powerConfigForm.week_days.join(','),
        is_enabled: powerConfigForm.is_enabled ? 1 : 0
      });
      message.success(`已批量更新 ${selectedDevices.length} 台设备的开关机配置`);
      setPowerConfigModalVisible(false);
      setSelectedDevices([]);
    } catch (error) {
      message.error('批量更新失败');
    } finally {
      setLoading(false);
    }
  };

  // 发送设备重启命令
  const handleRestartDevice = async () => {
    setLoading(true);
    try {
      await api.post(`/school/devices/${selectedDevice.id}/restart`);
      message.success('重启命令已发送');
    } catch (error) {
      message.error('发送重启命令失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取设备命令列表
  const fetchDeviceCommands = async (deviceId, page = 1) => {
    setCommandsLoading(true);
    try {
      const response = await api.get(`/school/devices/${deviceId}/commands`, {
        params: { page, pageSize: 5 }
      });
      setCommands(response.data.commands || []);
      setCommandsTotal(response.data.total || 0);
      setCommandsPage(page);
    } catch (error) {
      console.error('获取命令列表失败');
    } finally {
      setCommandsLoading(false);
    }
  };

  // 切换设备选择
  const handleDeviceSelect = (deviceId) => {
    setSelectedDevices(prev => 
      prev.includes(deviceId) 
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  // 全选设备
  const handleSelectAllDevices = () => {
    if (selectedDevices.length === devices.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(devices.map(d => d.id));
    }
  };

  // 时间格式转换
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  // 设置时间
  const setPowerTime = (type, time) => {
    if (type === 'on') {
      setPowerConfigForm(prev => ({ ...prev, power_on_time: `${time}:00` }));
    } else {
      setPowerConfigForm(prev => ({ ...prev, power_off_time: `${time}:00` }));
    }
  };

  // 设置设备时间
  const setDevicePowerTime = (type, time) => {
    if (type === 'on') {
      setDevicePowerConfig(prev => ({ ...prev, power_on_time: `${time}:00` }));
    } else {
      setDevicePowerConfig(prev => ({ ...prev, power_off_time: `${time}:00` }));
    }
  };

  const moveModule = (fromIndex, toIndex) => {
    const newModules = [...pageConfig.modules];
    const [removed] = newModules.splice(fromIndex, 1);
    newModules.splice(toIndex, 0, removed);
    newModules.forEach((mod, idx) => mod.position = idx);
    setPageConfig({ ...pageConfig, modules: newModules });
  };

  const movePreviewModule = (fromIdx, toIdx) => {
    const orderedModules = [...pageConfig.modules].sort((a, b) => a.position - b.position);
    const visibleModules = orderedModules.filter(mod => mod.visible !== false);
    const fromModule = visibleModules[fromIdx];

    if (!fromModule) return;

    const nextVisibleModules = [...visibleModules];
    const [removed] = nextVisibleModules.splice(fromIdx, 1);
    const nextIndex = Math.max(0, Math.min(toIdx, nextVisibleModules.length));
    nextVisibleModules.splice(nextIndex, 0, removed);

    let visibleIndex = 0;
    const newModules = orderedModules.map(mod => (
      mod.visible === false ? mod : nextVisibleModules[visibleIndex++]
    ));

    newModules.forEach((mod, idx) => mod.position = idx);
    setPageConfig({ ...pageConfig, modules: newModules });
  };

  const getPreviewDropIndex = (event, fromIdx, maxIndex) => {
    const moduleElements = Array.from(event.currentTarget.querySelectorAll('[data-preview-module-index]'))
      .map(element => ({
        index: Number(element.getAttribute('data-preview-module-index')),
        rect: element.getBoundingClientRect()
      }))
      .filter(item => item.index !== fromIdx);

    if (moduleElements.length === 0) {
      return 0;
    }

    const pointerX = event.clientX;
    const pointerY = event.clientY;
    const closest = moduleElements.reduce((best, item) => {
      const centerX = item.rect.left + item.rect.width / 2;
      const centerY = item.rect.top + item.rect.height / 2;
      const distance = ((pointerX - centerX) ** 2) + ((pointerY - centerY) ** 2);
      return distance < best.distance ? { ...item, distance } : best;
    }, { distance: Infinity });

    const closestCenterY = closest.rect.top + closest.rect.height / 2;
    const isAfterClosest = pointerY > closestCenterY;

    return Math.max(0, Math.min(maxIndex, closest.index + (isAfterClosest ? 1 : 0)));
  };

  const changeModuleSize = (moduleId, size) => {
    const newModules = pageConfig.modules.map(mod => 
      mod.id === moduleId ? { ...mod, size } : mod
    );
    setPageConfig({ ...pageConfig, modules: newModules });
  };

  const changeModuleColor1 = (moduleId, color) => {
    const newModules = pageConfig.modules.map(mod => 
      mod.id === moduleId ? { ...mod, color1: color } : mod
    );
    setPageConfig({ ...pageConfig, modules: newModules });
  };

  const changeModuleColor2 = (moduleId, color) => {
    const newModules = pageConfig.modules.map(mod => 
      mod.id === moduleId ? { ...mod, color2: color } : mod
    );
    setPageConfig({ ...pageConfig, modules: newModules });
  };

  const changeModuleGradientType = (moduleId, gradientType) => {
    const newModules = pageConfig.modules.map(mod => 
      mod.id === moduleId ? { ...mod, gradientType } : mod
    );
    setPageConfig({ ...pageConfig, modules: newModules });
  };

  const changeModuleVisibility = (moduleId, visible) => {
    const newModules = pageConfig.modules.map(mod =>
      mod.id === moduleId ? { ...mod, visible } : mod
    );
    setPageConfig({ ...pageConfig, modules: newModules });
  };

  const resetPageConfigModules = () => {
    Modal.confirm({
      title: '确认重置',
      content: '确定要重置当前设备的页面模块配置吗？',
      onOk: () => {
        const newModules = availableModules.map((mod, index) => ({
          ...mod,
          position: index,
          visible: true
        }));
        setPageConfig({ ...pageConfig, modules: newModules });
        message.success('当前设备模块配置已重置');
      }
    });
  };

  const renderModuleGrid = () => {
    if (!pageConfig) return null;
    
    const sortedModules = [...pageConfig.modules].sort((a, b) => a.position - b.position);
    const visibleModules = sortedModules.filter(mod => mod.visible !== false);
    
    const gridStyle = {
      display: 'grid',
      gridTemplateColumns: 'repeat(10, 1fr)',
      gridTemplateRows: 'repeat(4, 1fr)',
      gap: '12px',
      background: pageConfig.bg_gradient || themeColors[0].bgGradient,
      backgroundSize: 'cover',
      padding: '16px',
      borderRadius: '16px',
      width: 'min(100%, 360px)',
      aspectRatio: '4 / 6',
      boxShadow: '0 0 20px rgba(0,0,0,0.3)'
    };

    const getModuleGradient = (mod) => {
      const color1 = mod.color1 || '#1890ff';
      const color2 = mod.color2 || '#096dd9';
      const gradientType = mod.gradientType || 'horizontal';
      
      if (gradientType === 'vertical') {
        return `linear-gradient(180deg, ${color1} 0%, ${color2} 100%)`;
      } else if (gradientType === 'radial') {
        return `radial-gradient(circle, ${color1} 0%, ${color2} 100%)`;
      } else {
        return `linear-gradient(90deg, ${color1} 0%, ${color2} 100%)`;
      }
    };

    const getModuleStyle = (mod) => {
      let gridColumn = 'span 4';
      let gridRow = 'span 1';
      
      if (mod.size === 'large') {
        gridColumn = 'span 6';
        gridRow = 'span 2';
      } else if (mod.size === 'medium') {
        gridColumn = 'span 6';
        gridRow = 'span 1';
      } else {
        gridColumn = 'span 4';
        gridRow = 'span 1';
      }
      
      return {
        gridColumn,
        gridRow,
        background: getModuleGradient(mod),
        borderRadius: '12px',
        padding: '12px',
        color: pageConfig.text_color || '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'move',
        transition: 'transform 0.2s, box-shadow 0.2s',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        border: `1px solid ${pageConfig.border_color || '#333'}`
      };
    };

    return (
      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <div
          style={gridStyle}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const fromIdx = parseInt(e.dataTransfer.getData('previewIdx'));
            if (Number.isNaN(fromIdx)) return;
            const targetIdx = getPreviewDropIndex(e, fromIdx, visibleModules.length);
            movePreviewModule(fromIdx, targetIdx);
          }}
        >
          {visibleModules.map((mod, idx) => (
            <div
              key={mod.id}
              data-preview-module-index={idx}
              style={getModuleStyle(mod)}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('previewIdx', idx);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const fromIdx = parseInt(e.dataTransfer.getData('previewIdx'));
                if (Number.isNaN(fromIdx)) return;
                movePreviewModule(fromIdx, idx);
              }}
              onDragOver={(e) => e.preventDefault()}
              onDragEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
              onDragLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              onDragEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <span style={{ fontSize: mod.size === 'large' ? '36px' : mod.size === 'medium' ? '24px' : '18px' }}>{mod.icon}</span>
              <span style={{ marginTop: '8px', fontSize: '14px', fontWeight: '500' }}>{mod.name}</span>
              <span style={{ fontSize: '11px', opacity: 0.7, marginTop: '2px' }}>
                {mod.size === 'large' ? '大(6×2)' : mod.size === 'medium' ? '中(6×1)' : '小(4×1)'}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ backgroundColor: '#fff', padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 'bold' }}>学校管理平台</h1>
          <span style={{ marginLeft: 16, color: '#8c8c8c' }}>{teacher.school_name}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: 16 }}>欢迎, {teacher.name}</span>
          <span style={{ marginRight: 16 }}>角色: {teacher.role}</span>
          <Button onClick={onLogout}>退出登录</Button>
        </div>
      </Header>

      <Layout>
        <Sider width={200} style={{ backgroundColor: '#fff' }}>
          <Menu
            mode="inline"
            selectedKeys={[currentPage]}
            defaultOpenKeys={['class-board', 'moral-machine']}
            style={{ height: '100%', borderRight: 0 }}
          >
            <Menu.SubMenu key="class-board" icon={<LaptopOutlined />} title="电子班牌">
              <Menu.Item
                key="class-board-devices"
                onClick={() => setCurrentPage('class-board-devices')}
              >
                设备管理
              </Menu.Item>
              <Menu.Item
                key="third-party-apps"
                onClick={() => setCurrentPage('third-party-apps')}
              >
                <AppstoreOutlined /> 第三方应用
              </Menu.Item>
              <Menu.Item
                key="school-apps"
                onClick={() => setCurrentPage('school-apps')}
              >
                <AppstoreOutlined /> 学校系统应用
              </Menu.Item>
              <Menu.Item
                key="school-page-config"
                onClick={() => setCurrentPage('school-page-config')}
              >
                <LayoutOutlined /> 自定义模块
              </Menu.Item>
              {teacher.classes && teacher.classes.length > 0 && (
                <Menu.SubMenu key="classes" title="我的班级">
                  {teacher.classes.map(cls => (
                    <Menu.Item
                      key={`class-${cls.id}`}
                      onClick={() => {
                        setCurrentPage('class-board-devices');
                        setSelectedClass(selectedClass === cls.id ? null : cls.id);
                      }}
                    >
                      {cls.name} ({cls.student_count}人)
                    </Menu.Item>
                  ))}
                </Menu.SubMenu>
              )}
            </Menu.SubMenu>
            <Menu.SubMenu key="moral-machine" icon={<LaptopOutlined />} title="德育机">
              <Menu.Item
                key="moral-machine-devices"
                onClick={() => setCurrentPage('moral-machine-devices')}
              >
                设备管理
              </Menu.Item>
            </Menu.SubMenu>
          </Menu>
        </Sider>

        <Content style={{ padding: '24px', backgroundColor: '#f5f5f5' }}>
          {(currentPage === 'class-board-devices' || currentPage === 'moral-machine-devices') && (
            <Card title={`${currentDeviceType}设备列表`} extra={
              <div style={{ display: 'flex', gap: 12 }}>
                {currentIsClassBoard && (
                  <Select
                    placeholder="选择班级"
                    style={{ width: 150 }}
                    value={selectedClass}
                    onChange={setSelectedClass}
                  >
                    <Select.Option value={null}>全部班级</Select.Option>
                    {teacher.classes?.map(cls => (
                      <Select.Option key={cls.id} value={cls.id}>{cls.name}</Select.Option>
                    ))}
                  </Select>
                )}
                <Button onClick={fetchDevices} icon={<SyncOutlined />}>刷新</Button>
                {currentIsClassBoard && (
                  <Button
                    type="primary"
                    icon={<PoweroffOutlined />}
                    disabled={selectedDevices.length === 0}
                    onClick={() => setPowerConfigModalVisible(true)}
                  >
                    开关机设置 ({selectedDevices.length})
                  </Button>
                )}
              </div>
            }>
              <Table
                rowSelection={currentIsClassBoard ? {
                  type: 'checkbox',
                  selectedRowKeys: selectedDevices,
                  onChange: (selectedRowKeys) => setSelectedDevices(selectedRowKeys)
                } : undefined}
                columns={[
                  { title: '设备名称', dataIndex: 'name', key: 'name' },
                  { title: '设备标识', dataIndex: 'device_id', key: 'device_id' },
                  { title: '学校', dataIndex: 'school_name', key: 'school_name' },
                  ...(currentIsClassBoard ? [{ title: '班级', dataIndex: 'class_name', key: 'class_name' }] : []),
                  { title: '状态', dataIndex: 'status', key: 'status', render: status => (
                    <Tag color={status === '在线' ? 'green' : 'red'}>{status}</Tag>
                  )},
                  { title: '电池', dataIndex: 'battery', key: 'battery', render: battery => `${battery}%` },
                  { title: '软件版本', dataIndex: 'version', key: 'version', render: version => version || '-' },
                  { title: '操作', key: 'actions', render: (_, device) => (
                    currentIsClassBoard ? (
                      <>
                        <Dropdown overlay={
                          <DropdownMenu>
                            <DropdownMenu.Item onClick={() => fetchPageConfig(device)}>页面配置</DropdownMenu.Item>
                            <DropdownMenu.Item onClick={() => {
                              setSelectedDevice(device);
                              fetchDeviceApps(device.id);
                              setStudentAppModalVisible(true);
                            }}>学生应用</DropdownMenu.Item>
                            <DropdownMenu.Item onClick={() => {
                              setSelectedDevice(device);
                              fetchDeviceClassApps(device.id);
                              setClassAppModalVisible(true);
                            }}>班级应用</DropdownMenu.Item>
                            <DropdownMenu.Item onClick={() => {
                              setSelectedDevice(device);
                              setDeviceDetailModalVisible(true);
                              fetchDeviceDetailData(device);
                            }}>设备详情</DropdownMenu.Item>
                          </DropdownMenu>
                        }>
                          <Button size="small">操作</Button>
                        </Dropdown>
                        <Button
                          size="small"
                          style={{ marginLeft: 8 }}
                          onClick={() => {
                            Modal.confirm({
                              title: '确认重启',
                              content: `确定要重启设备 "${device.name}" 吗？重启过程中设备将暂时不可用。`,
                              okText: '确定重启',
                              cancelText: '取消',
                              onOk: async () => {
                                try {
                                  await api.post(`/school/devices/${device.id}/control/power`, { action: 'restart' });
                                  message.success('重启指令已发送');
                                } catch (error) {
                                  message.error('发送失败');
                                }
                              }
                            });
                          }}
                        >重启</Button>
                      </>
                    ) : (
                      <>
                        <Dropdown overlay={
                          <DropdownMenu>
                            <DropdownMenu.Item onClick={() => {
                              setSelectedDevice(device);
                              setDeviceDetailModalVisible(true);
                              fetchDeviceDetailData(device);
                            }}>设备详情</DropdownMenu.Item>
                          </DropdownMenu>
                        }>
                          <Button size="small">操作</Button>
                        </Dropdown>
                        <Button
                          size="small"
                          style={{ marginLeft: 8 }}
                          onClick={() => {
                            Modal.confirm({
                              title: '确认重启',
                              content: `确定要重启设备 "${device.name}" 吗？重启过程中设备将暂时不可用。`,
                              okText: '确定重启',
                              cancelText: '取消',
                              onOk: async () => {
                                try {
                                  await api.post(`/school/devices/${device.id}/control/power`, { action: 'restart' });
                                  message.success('重启指令已发送');
                                } catch (error) {
                                  message.error('发送失败');
                                }
                              }
                            });
                          }}
                        >重启</Button>
                      </>
                    )
                  )}
                ]}
                dataSource={devices}
                rowKey="id"
              />
            </Card>
          )}
          {currentPage === 'third-party-apps' && (
            <ThirdPartyAppsPage teacher={teacher} />
          )}
          {currentPage === 'school-apps' && (
            <SchoolAppsManagement />
          )}
          {currentPage === 'school-page-config' && (
            <SchoolPageConfigPage teacher={teacher} />
          )}

          {/* 设备详情卡片 */}
          {selectedDevice && (
            <Card title="设备详情" style={{ marginTop: 16 }}>
              <Descriptions bordered column={2}>
                <Descriptions.Item label="设备名称">{selectedDevice.name}</Descriptions.Item>
                <Descriptions.Item label="设备标识">{selectedDevice.device_id}</Descriptions.Item>
                <Descriptions.Item label="学校">{selectedDevice.school_name}</Descriptions.Item>
                <Descriptions.Item label="班级">{selectedDevice.class_name}</Descriptions.Item>
                <Descriptions.Item label="设备类型">{selectedDevice.type}</Descriptions.Item>
                <Descriptions.Item label="状态">
                  <Tag color={selectedDevice.status === '在线' ? 'green' : 'red'}>
                    {selectedDevice.status}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="电池电量">{selectedDevice.battery}%</Descriptions.Item>
                <Descriptions.Item label="IP地址">{selectedDevice.ip_address}</Descriptions.Item>
                <Descriptions.Item label="最后心跳" span={2}>
                  {selectedDevice.last_heartbeat ? new Date(selectedDevice.last_heartbeat).toLocaleString() : '未知'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}
        </Content>
      </Layout>

      {/* 控制模态框 */}
      <Modal
        title={`${controlType === 'power' ? '电源控制' : controlType === 'screen' ? '屏幕控制' : '应用控制'}`}
        open={controlModalVisible}
        onCancel={() => setControlModalVisible(false)}
        footer={null}
      >
        <div style={{ display: 'flex', gap: 16 }}>
          {controlType === 'power' && (
            <>
              <Button type="primary" onClick={() => handleControl('on')} loading={loading}>开机</Button>
              <Button danger onClick={() => handleControl('off')} loading={loading}>关机</Button>
              <Button onClick={() => handleControl('restart')} loading={loading}>重启</Button>
            </>
          )}
          {controlType === 'screen' && (
            <>
              <Button type="primary" onClick={() => handleControl('on')} loading={loading}>亮屏</Button>
              <Button onClick={() => handleControl('off')} loading={loading}>息屏</Button>
            </>
          )}
          {controlType === 'app' && (
            <>
              <Button type="primary" onClick={() => handleControl('start')} loading={loading}>启动</Button>
              <Button onClick={() => handleControl('stop')} loading={loading}>停止</Button>
            </>
          )}
        </div>
      </Modal>

      {/* 设备详情模态框 */}
      <Modal
        title={`设备详情 - ${selectedDevice?.name || ''}`}
        open={deviceDetailModalVisible}
        onCancel={() => { setDeviceDetailModalVisible(false); setDeviceDetailData(null); setDeviceStudentStatus(null); setQueryResult(null); }}
        footer={null}
        width={800}
      >
        {deviceDetailData && (
          <div>
            <h3>设备基础信息</h3>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="设备名称">{deviceDetailData.name}</Descriptions.Item>
              <Descriptions.Item label="设备标识">{deviceDetailData.device_id}</Descriptions.Item>
              <Descriptions.Item label="设备类型">{deviceDetailData.type}</Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={deviceDetailData.status === '在线' ? 'green' : 'red'}>
                  {deviceDetailData.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="电池电量">{deviceDetailData.battery}%</Descriptions.Item>
              <Descriptions.Item label="IP地址">{deviceDetailData.ip_address || '未知'}</Descriptions.Item>
              <Descriptions.Item label="学校">{deviceDetailData.school_name}</Descriptions.Item>
              <Descriptions.Item label="班级">{deviceDetailData.class_name}</Descriptions.Item>
            </Descriptions>

            {isClassBoard(deviceDetailData.type) && (
              <>
                <Divider />

                <h3>学生数据同步</h3>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    <Button type="primary" onClick={handleTriggerStudentReport} loading={loading}>
                      触发设备上报学生数据
                    </Button>
                    <Button onClick={() => fetchDeviceStudentStatus(selectedDevice.id)} loading={loading}>
                      刷新状态
                    </Button>
                    <Button onClick={() => setSyncModalVisible(true)} loading={loading}>
                      同步学生数据
                    </Button>
                  </div>

                  {syncStatus && (
                    <div style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4, marginBottom: 16 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                        <div>
                          <span style={{ fontWeight: 'bold' }}>同步状态: </span>
                          <Tag color={syncStatus.is_synced ? 'green' : 'red'}>
                            {syncStatus.is_synced ? '数据一致' : '数据不一致'}
                          </Tag>
                        </div>
                        <div>
                          <span style={{ fontWeight: 'bold' }}>最后同步: </span>
                          {syncStatus.last_sync_time ? new Date(syncStatus.last_sync_time).toLocaleString() : '从未同步'}
                        </div>
                        <div>
                          <span style={{ fontWeight: 'bold' }}>服务器学生数: </span>
                          {syncStatus.server_student_count} 人
                        </div>
                        <div>
                          <span style={{ fontWeight: 'bold' }}>设备学生数: </span>
                          {syncStatus.device_student_count} 人
                        </div>
                      </div>
                    </div>
                  )}

                  {deviceStudentStatus && (
                    <div style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4, marginBottom: 16 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                        <div><span style={{ fontWeight: 'bold' }}>学生总数: </span>{deviceStudentStatus.total_count} 人</div>
                        <div><span style={{ fontWeight: 'bold' }}>有效数据: </span>{deviceStudentStatus.valid_count} 条</div>
                        <div><span style={{ fontWeight: 'bold' }}>无效数据: </span><span style={{ color: deviceStudentStatus.invalid_count > 0 ? '#ff4d4f' : 'inherit' }}>{deviceStudentStatus.invalid_count} 条</span></div>
                      </div>
                    </div>
                  )}
                </div>

                <Divider />

                <h4>学生查询</h4>
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  <Input
                    placeholder="请输入学生卡号"
                    value={queryCardNumber}
                    onChange={(e) => setQueryCardNumber(e.target.value)}
                    style={{ width: 200 }}
                    onPressEnter={handleQueryStudent}
                  />
                  <Button type="primary" onClick={handleQueryStudent} loading={loading}>
                    查询
                  </Button>
                </div>
                {queryResult && (
                  <div style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                    {queryResult.found ? (
                      <div>
                        <p>查询结果：找到学生</p>
                        <p>姓名: {queryResult.student?.name}</p>
                        <p>班级: {queryResult.student?.class_name}</p>
                        <p>卡号: {queryResult.student?.card_id}</p>
                      </div>
                    ) : (
                      <p style={{ color: '#ff4d4f' }}>未找到该学生信息</p>
                    )}
                  </div>
                )}
              </>
            )}

            <Divider />

            <h3>开关机配置</h3>
            <div style={{ marginBottom: 16 }}>
              <Button onClick={() => {
                fetchDevicePowerConfig(selectedDevice.id);
              }} loading={loading}>
                <ClockCircleOutlined /> 刷新配置
              </Button>
              <Button onClick={handleRestartDevice} loading={loading} style={{ marginLeft: 8 }}>
                <PlayCircleOutlined /> 重启设备
              </Button>
            </div>
            {devicePowerConfig && (
              <div style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4, marginBottom: 16 }}>
                <Form layout="horizontal">
                  <Form.Item label="开机时间" style={{ marginBottom: 12 }}>
                    <TimePicker
                      value={timeStrToDate(devicePowerConfig.power_on_time)}
                      onChange={(time) => setDevicePowerTime('on', time?.format('HH:mm'))}
                      style={{ width: 150 }}
                    />
                  </Form.Item>
                  <Form.Item label="关机时间" style={{ marginBottom: 12 }}>
                    <TimePicker
                      value={timeStrToDate(devicePowerConfig.power_off_time)}
                      onChange={(time) => setDevicePowerTime('off', time?.format('HH:mm'))}
                      style={{ width: 150 }}
                    />
                  </Form.Item>
                  <Form.Item label="重复日期" style={{ marginBottom: 12 }}>
                    <Checkbox.Group
                      options={weekDayOptions}
                      value={Array.isArray(devicePowerConfig.week_days) ? devicePowerConfig.week_days : devicePowerConfig.week_days?.split(',') || []}
                      onChange={(values) => setDevicePowerConfig(prev => ({ ...prev, week_days: values }))}
                    />
                  </Form.Item>
                  <Form.Item label="启用定时" style={{ marginBottom: 12 }}>
                    <Switch
                      checked={devicePowerConfig.is_enabled === 1}
                      onChange={(checked) => setDevicePowerConfig(prev => ({ ...prev, is_enabled: checked ? 1 : 0 }))}
                    />
                  </Form.Item>
                </Form>
                <Button type="primary" onClick={handleUpdateDevicePowerConfig} loading={loading}>
                  保存配置
                </Button>
              </div>
            )}

            <Divider />

            <h3>指令日志</h3>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              {commandsLoading ? (
                <p>加载中...</p>
              ) : commands.length > 0 ? (
                <div>
                  <Table
                    dataSource={commands}
                    rowKey="id"
                    pagination={false}
                    columns={[
                      { title: '指令类型', dataIndex: 'command', key: 'command' },
                      { title: '操作', dataIndex: 'action', key: 'action' },
                      { title: '状态', dataIndex: 'status', key: 'status', render: status => {
                        const successStatuses = ['completed', 'executed'];
                        const statusTextMap = {
                          pending: '待执行',
                          executing: '执行中',
                          completed: '已完成',
                          executed: '成功',
                          failed: '失败'
                        };
                        return (
                          <Tag color={successStatuses.includes(status) ? 'green' : status === 'pending' ? 'orange' : status === 'executing' ? 'blue' : 'red'}>
                            {statusTextMap[status] || status}
                          </Tag>
                        );
                      }},
                      { title: '创建时间', dataIndex: 'created_at', key: 'created_at', render: time => time ? new Date(time).toLocaleString() : '' },
                      { title: '完成时间', dataIndex: 'completed_at', key: 'completed_at', render: (_, record) => {
                        const time = record.completed_at || record.executed_at;
                        return time ? new Date(time).toLocaleString() : '';
                      }}
                    ]}
                  />
                  <Pagination
                    current={commandsPage}
                    total={commandsTotal}
                    pageSize={5}
                    onChange={(page) => fetchDeviceCommands(selectedDevice.id, page)}
                    style={{ marginTop: 16, textAlign: 'right' }}
                  />
                </div>
              ) : (
                <p>暂无指令记录</p>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* 同步模态框 */}
      <Modal
        title="同步学生数据"
        open={syncModalVisible}
        onOk={handleSyncStudents}
        onCancel={() => setSyncModalVisible(false)}
        okText="确定"
        cancelText="取消"
        confirmLoading={loading}
      >
        <p>确定要触发学生数据同步吗？</p>
        <p style={{ color: '#8c8c8c', fontSize: 12 }}>同步将覆盖设备上所有学生数据</p>
      </Modal>

      {/* 页面配置模态框 */}
      <Modal
        title={`设备页面配置 - ${selectedDevice?.name || ''}`}
        open={pageConfigModalVisible}
        onOk={savePageConfig}
        onCancel={() => setPageConfigModalVisible(false)}
        width={1100}
        okText="保存当前设备配置"
        cancelText="取消"
        confirmLoading={loading}
      >
        {pageConfig && (
          <div style={{ display: 'flex', gap: 24, minHeight: 600 }}>
            <div style={{ width: 400, flex: 'none' }}>
              <Form layout="vertical">
                <Form.Item
                  label={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>模块配置</span>
                      <Button size="small" onClick={resetPageConfigModules}>重置</Button>
                    </div>
                  }
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: 12,
                      maxHeight: 500,
                      overflowY: 'auto'
                    }}
                  >
                    {[...pageConfig.modules].sort((a, b) => a.position - b.position).map((mod, idx) => {
                      const color1 = mod.color1 || '#1890ff';
                      const color2 = mod.color2 || '#096dd9';
                      const gradientType = mod.gradientType || 'horizontal';
                      const background = gradientType === 'vertical'
                        ? `linear-gradient(180deg, ${color1} 0%, ${color2} 100%)`
                        : gradientType === 'radial'
                          ? `radial-gradient(circle, ${color1} 0%, ${color2} 100%)`
                          : `linear-gradient(90deg, ${color1} 0%, ${color2} 100%)`;

                      return (
                        <div
                          key={mod.id}
                          style={{
                            background,
                            padding: '12px',
                            borderRadius: 8,
                            color: 'white',
                            cursor: 'move',
                            minHeight: 90,
                            opacity: mod.visible === false ? 0.45 : 1
                          }}
                          draggable
                          onDragStart={(e) => e.dataTransfer.setData('targetIdx', idx)}
                          onDrop={(e) => {
                            e.preventDefault();
                            const fromIdx = parseInt(e.dataTransfer.getData('targetIdx'));
                            moveModule(fromIdx, idx);
                          }}
                          onDragOver={(e) => e.preventDefault()}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <span style={{ fontSize: '20px' }}>{mod.icon}</span>
                            <span style={{ flex: 1, fontWeight: '500' }}>{mod.name}</span>
                          </div>
                          <div style={{ fontSize: '12px', opacity: 0.85, marginBottom: 10 }}>
                            {mod.size === 'large' ? '大模块' : mod.size === 'medium' ? '中模块' : '小模块'}
                          </div>
                          <div style={{ display: 'none' }}>
                            <input
                              type="color"
                              value={color1}
                              onChange={(e) => changeModuleColor1(mod.id, e.target.value)}
                            />
                            <input
                              type="color"
                              value={color2}
                              onChange={(e) => changeModuleColor2(mod.id, e.target.value)}
                            />
                            <Select
                              value={gradientType}
                              onChange={(type) => changeModuleGradientType(mod.id, type)}
                              options={gradientTypeOptions}
                            />
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', opacity: 0.9 }}>显示</span>
                            <Switch
                              size="small"
                              checked={mod.visible !== false}
                              onChange={(checked) => changeModuleVisibility(mod.id, checked)}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Form.Item>
              </Form>
            </div>
            {/* 左侧配置面板 */}
            <div style={{ width: 320, flex: 'none', display: 'none' }}>
              <Form layout="vertical">
                {/* 主题色选择 */}
                <Form.Item label="主题风格（含渐变背景）">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {themeColors.map((color, index) => (
                      <button
                        key={index}
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: '8px',
                          background: color.bgGradient,
                          border: pageConfig.bg_gradient === color.bgGradient ? '3px solid #000' : '1px solid #ccc',
                          cursor: 'pointer'
                        }}
                        onClick={() => setPageConfig({ 
                          ...pageConfig, 
                          bg_gradient: color.bgGradient,
                          text_color: color.textColor,
                          border_color: color.borderColor
                        })}
                        title={color.name}
                      />
                    ))}
                  </div>
                </Form.Item>

                {/* 自定义颜色 */}
                <Form.Item label="文字颜色">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="color"
                      value={pageConfig.text_color || '#ffffff'}
                      onChange={(e) => setPageConfig({ ...pageConfig, text_color: e.target.value })}
                      style={{ width: 40, height: 40, borderRadius: 8, cursor: 'pointer' }}
                    />
                    <span>{pageConfig.text_color || '#ffffff'}</span>
                  </div>
                </Form.Item>

                <Form.Item label="边框颜色">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="color"
                      value={pageConfig.border_color || '#333333'}
                      onChange={(e) => setPageConfig({ ...pageConfig, border_color: e.target.value })}
                      style={{ width: 40, height: 40, borderRadius: 8, cursor: 'pointer' }}
                    />
                    <span>{pageConfig.border_color || '#333333'}</span>
                  </div>
                </Form.Item>

                {/* 背景图片 */}
                <Form.Item label="背景图片URL">
                  <Input
                    value={pageConfig.background_image}
                    onChange={(e) => setPageConfig({ ...pageConfig, background_image: e.target.value })}
                    placeholder="输入背景图片URL（可选）"
                  />
                </Form.Item>

                <Divider />

                {/* 模块排列设置 */}
                <Form.Item label="模块排列与颜色">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {pageConfig.modules.map((mod, idx) => (
                      <div
                        key={mod.id}
                        style={{
                          background: `linear-gradient(90deg, ${mod.color1 || '#1890ff'} 0%, ${mod.color2 || '#096dd9'} 100%)`,
                          padding: '12px',
                          borderRadius: 8,
                          color: 'white',
                          cursor: 'move'
                        }}
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('targetIdx', idx)}
                        onDrop={(e) => {
                          e.preventDefault();
                          const fromIdx = e.dataTransfer.getData('targetIdx');
                          moveModule(fromIdx, idx);
                        }}
                        onDragOver={(e) => e.preventDefault()}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <span style={{ fontSize: '18px' }}>{mod.icon}</span>
                          <span style={{ flex: 1, fontWeight: '500' }}>{mod.name}</span>
                          <span style={{ fontSize: '12px', opacity: 0.8 }}>
                            {mod.size === 'large' ? '大' : mod.size === 'medium' ? '中' : '小'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '12px', opacity: 0.8 }}>颜色1:</span>
                          <input
                            type="color"
                            value={mod.color1 || '#1890ff'}
                            onChange={(e) => changeModuleColor1(mod.id, e.target.value)}
                            style={{ width: 32, height: 32, borderRadius: 6, cursor: 'pointer', border: 'none' }}
                          />
                          <span style={{ fontSize: '12px', opacity: 0.8 }}>颜色2:</span>
                          <input
                            type="color"
                            value={mod.color2 || '#096dd9'}
                            onChange={(e) => changeModuleColor2(mod.id, e.target.value)}
                            style={{ width: 32, height: 32, borderRadius: 6, cursor: 'pointer', border: 'none' }}
                          />
                          <Select
                            value={mod.gradientType || 'horizontal'}
                            onChange={(type) => changeModuleGradientType(mod.id, type)}
                            style={{ width: 70, backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '12px' }}
                            options={gradientTypeOptions}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Form.Item>

                <Divider />

                {/* 尺寸说明 */}
                <div style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 8 }}>
                  <p style={{ margin: 0, fontWeight: 'bold', fontSize: 14 }}>尺寸说明（横10×竖4）</p>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: 20, fontSize: 12, color: '#666' }}>
                    <li>大块：横6 × 竖2</li>
                    <li>中块：横6 × 竖1</li>
                    <li>小块：横4 × 竖1</li>
                  </ul>
                </div>
              </Form>
            </div>

            {/* 右侧预览区域 */}
            <div style={{ flex: 1 }}>
              <Form layout="vertical">
                <Form.Item label="Pad竖屏预览（21.5寸模拟）">
                  {renderModuleGrid()}
                </Form.Item>
              </Form>
            </div>
          </div>
        )}
      </Modal>

      {/* 学生应用配置模态框 */}
      <Modal
        title={`学生应用配置 - ${selectedDevice?.name || ''}`}
        open={studentAppModalVisible}
        onOk={handleUpdateDeviceApps}
        onCancel={() => { setStudentAppModalVisible(false); setDeviceApps([]); }}
        confirmLoading={appsLoading}
        width={700}
      >
        {appsLoading ? (
          <p>加载中...</p>
        ) : (
          <div>
            <Divider orientation="left">主题应用</Divider>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
              {deviceApps.filter(app => app.app_type === 'theme').map(app => (
                <div
                  key={app.id}
                  style={{
                    padding: 16,
                    border: app.is_enabled ? '2px solid #1890ff' : '2px solid #d9d9d9',
                    borderRadius: 8,
                    width: 150,
                    backgroundColor: app.is_enabled ? '#e6f7ff' : '#fff',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleAppEnabled(app.id)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontWeight: 'bold' }}>{app.app_name}</span>
                    <Switch
                      checked={app.is_enabled}
                      onChange={(checked, e) => {
                        e.stopPropagation();
                        toggleAppEnabled(app.id);
                      }}
                    />
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {app.description || '暂无描述'}
                  </div>
                </div>
              ))}
            </div>

            <Divider orientation="left">分类应用</Divider>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {deviceApps.filter(app => app.app_type === 'category').map(app => (
                <div
                  key={app.id}
                  style={{
                    padding: 16,
                    border: app.is_enabled ? '2px solid #1890ff' : '2px solid #d9d9d9',
                    borderRadius: 8,
                    width: 150,
                    backgroundColor: app.is_enabled ? '#e6f7ff' : '#fff',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleAppEnabled(app.id)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontWeight: 'bold' }}>{app.app_name}</span>
                    <Switch
                      checked={app.is_enabled}
                      onChange={(checked, e) => {
                        e.stopPropagation();
                        toggleAppEnabled(app.id);
                      }}
                    />
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {app.description || '暂无描述'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      {/* 班级应用配置模态框 */}
      <Modal
        title={`班级应用配置 - ${selectedDevice?.name || ''}`}
        open={classAppModalVisible}
        onOk={handleUpdateDeviceClassApps}
        onCancel={() => { setClassAppModalVisible(false); setDeviceClassApps([]); }}
        confirmLoading={appsLoading}
        width={700}
      >
        {appsLoading ? (
          <p>加载中...</p>
        ) : (
          <div>
            <Divider orientation="left">主题应用</Divider>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
              {deviceClassApps.filter(app => app.app_type === 'theme').map(app => (
                <div
                  key={app.id}
                  style={{
                    padding: 16,
                    border: app.is_enabled ? '2px solid #1890ff' : '2px solid #d9d9d9',
                    borderRadius: 8,
                    width: 150,
                    backgroundColor: app.is_enabled ? '#e6f7ff' : '#fff',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleClassAppEnabled(app.id)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontWeight: 'bold' }}>{app.app_name}</span>
                    <Switch
                      checked={app.is_enabled}
                      onChange={(checked, e) => {
                        e.stopPropagation();
                        toggleClassAppEnabled(app.id);
                      }}
                    />
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {app.description || '暂无描述'}
                  </div>
                </div>
              ))}
            </div>

            <Divider orientation="left">分类应用</Divider>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {deviceClassApps.filter(app => app.app_type === 'category').map(app => (
                <div
                  key={app.id}
                  style={{
                    padding: 16,
                    border: app.is_enabled ? '2px solid #1890ff' : '2px solid #d9d9d9',
                    borderRadius: 8,
                    width: 150,
                    backgroundColor: app.is_enabled ? '#e6f7ff' : '#fff',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleClassAppEnabled(app.id)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontWeight: 'bold' }}>{app.app_name}</span>
                    <Switch
                      checked={app.is_enabled}
                      onChange={(checked, e) => {
                        e.stopPropagation();
                        toggleClassAppEnabled(app.id);
                      }}
                    />
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {app.description || '暂无描述'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      {/* 批量开关机配置模态框 */}
      <Modal
        title="批量设置开关机配置"
        open={powerConfigModalVisible}
        onOk={handleBatchPowerConfig}
        onCancel={() => {
          setPowerConfigModalVisible(false);
          setPowerConfigForm({
            power_on_time: '07:30:00',
            power_off_time: '18:00:00',
            week_days: ['1', '2', '3', '4', '5'],
            is_enabled: true
          });
        }}
        confirmLoading={loading}
        width={500}
      >
        <p>已选择 {selectedDevices.length} 台设备进行批量配置</p>
        <Form layout="vertical">
          <Form.Item label="开机时间">
            <TimePicker
              value={timeStrToDate(powerConfigForm.power_on_time)}
              onChange={(time) => setPowerTime('on', time?.format('HH:mm'))}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item label="关机时间">
            <TimePicker
              value={timeStrToDate(powerConfigForm.power_off_time)}
              onChange={(time) => setPowerTime('off', time?.format('HH:mm'))}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item label="重复日期">
            <Checkbox.Group
              options={weekDayOptions}
              value={powerConfigForm.week_days}
              onChange={(values) => setPowerConfigForm(prev => ({ ...prev, week_days: values }))}
            />
          </Form.Item>
          <Form.Item label="启用定时">
            <Switch
              checked={powerConfigForm.is_enabled}
              onChange={(checked) => setPowerConfigForm(prev => ({ ...prev, is_enabled: checked }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default SchoolDashboard;
